// Guided setup's server state (spec/13 §13.1, F-HQ-03; `packages/contracts/src/routes/setup.ts`), as TanStack Query
// hooks over the generated client:
//
//   GET    /v1/organizations/{o}/setup                            the organisation's steps and each of its venues'
//   PUT    /v1/organizations/{o}/setup/skips/{step}               skip the organisation's suggested step
//   DELETE /v1/organizations/{o}/setup/skips/{step}               take that skip back
//   GET    /v1/organizations/{o}/venues/{v}/setup                 one venue's steps
//   PUT    /v1/organizations/{o}/venues/{v}/setup/skips/{step}    skip a suggested step
//   DELETE /v1/organizations/{o}/venues/{v}/setup/skips/{step}    take a skip back
//
// Every step is computed by the server from the venue's state when it is read, so the page re-reads on focus (an owner
// sets the hours in another tab, comes back, and sees it ticked) and after any write the console makes (`app.tsx`).

import type { OrganizationSetup, SuggestedOrganizationSetupStepId, SuggestedVenueSetupStepId, VenueSetup } from '@clavian/api-client';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { useApi } from '../api/client';

export const setupKeys = {
  all: ['setup'] as const,
  organization: (organizationId: string) => ['setup', 'organization', organizationId] as const,
  venue: (organizationId: string, venueId: string) => ['setup', 'venue', organizationId, venueId] as const,
};

/**
 * A step is ticked by something done on another screen, so a cached answer is never trusted: re-read on every mount and
 * whenever the window regains focus, instead of the console's 30-second default.
 */
const ALWAYS_FRESH = { staleTime: 0, refetchOnMount: 'always', refetchOnWindowFocus: 'always' } as const;

/** Re-read every setup view: a step changed somewhere (a venue went live, a station was paired, a skip). */
export function invalidateSetup(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: setupKeys.all });
}

export function useOrganizationSetup(organizationId: string, enabled = true) {
  const api = useApi();
  return useQuery<OrganizationSetup>({
    queryKey: setupKeys.organization(organizationId),
    queryFn: () => api.getOrganizationSetup({ path: { organizationId } }),
    enabled: enabled && organizationId !== '',
    ...ALWAYS_FRESH,
    retry: false,
  });
}

export function useVenueSetup(organizationId: string, venueId: string, enabled = true) {
  const api = useApi();
  return useQuery<VenueSetup>({
    queryKey: setupKeys.venue(organizationId, venueId),
    queryFn: () => api.getVenueSetup({ path: { organizationId, venueId } }),
    enabled: enabled && organizationId !== '' && venueId !== '',
    ...ALWAYS_FRESH,
    retry: false,
  });
}

/** Skip the organisation's suggested step, or take the skip back. Answers with its setup now, which replaces the cached one. */
export function useSkipOrganizationStep(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ step, skip }: { readonly step: SuggestedOrganizationSetupStepId; readonly skip: boolean }) =>
      skip ? api.skipOrganizationSetupStep({ path: { organizationId, step } }) : api.unskipOrganizationSetupStep({ path: { organizationId, step } }),
    onSuccess: (setup) => queryClient.setQueryData(setupKeys.organization(organizationId), setup),
  });
}

/** Skip a suggested step, or take the skip back. Answers with the venue's setup now, which replaces the cached one. */
export function useSkipStep(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ step, skip }: { readonly step: SuggestedVenueSetupStepId; readonly skip: boolean }) =>
      skip
        ? api.skipVenueSetupStep({ path: { organizationId, venueId, step } })
        : api.unskipVenueSetupStep({ path: { organizationId, venueId, step } }),
    onSuccess: (setup) => {
      queryClient.setQueryData(setupKeys.venue(organizationId, venueId), setup);
      void queryClient.invalidateQueries({ queryKey: setupKeys.organization(organizationId) });
    },
  });
}
